import { REPEAT_MODE_PLAYLIST, REPEAT_MODE_NONE } from '../constants';
import EventTarget from '../utils/event-target';
import isString from '../utils/is-string';
import isUndefined from '../utils/is-undefined';
import logger from '../../utils/logger';

const EVENT_CHANGE = 'change';
const EVENT_SELECT = 'select';

let lastPlaylistItemId = 0;

class PlaylistItem {
  constructor (data) {
    this.id = ++lastPlaylistItemId;
    this.metadata = data.metadata;
    this.ads = data.ads;
    this.text = data.text;
    this.audio = data.audio;
    this.links = data.links;
    this.tags = data.tags;
  }

  /**
   * Create a new playlist item from it's source data
   *
   * @param  {Object} data
   * @return {PlaylistItem}
   */
  static create (data) {
    return new PlaylistItem(data);
  }
}

export default class PlaylistController extends EventTarget {
  constructor (player, options) {
    super();

    this.player = player;
    this.logger = options.logger || logger;
    this.playlist = [];
    this.playlistIndex = -1;
    this.uidIndex = {};
    this.repeat_ = options.repeat || REPEAT_MODE_NONE;
  }

  /**
   * Change the repeat mode on the playlist
   * @param {string} mode
   */
  setRepeat (mode) {
    this.repeat_ = mode;
  }

  /**
   * Loads a playlist from the loaded playback source
   *
   * @param {event} event
   */
  setPlaylist (playlist) {
    this.logger.debug('Playlist: Changing the active playlist');

    playlist.items = playlist.items.map(PlaylistItem.create);
    this.playlist = playlist;
    this.playlistIndex = -1;

    // index playlist items by thier id
    this.reIndex();

    this.trigger({
      type: EVENT_CHANGE,
      playlist: this.playlist,
      added: 0,
      loaded: playlist.items.length,
      total: this.playlist.items.length
    });
  }

  /**
   * Re-index the playlist
   */
  reIndex () {
    this.uidIndex = this.playlist.items.reduce(function (uidIndex, item, index) {
      uidIndex[item.metadata.uid] = index;
      return uidIndex;
    }, {});
  }

  /**
   * Adds playlist items to the current playlist
   *
   * @param {Object} playlist The new playlist items to be added
   */
  addPlaylistItems (playlist) {
    this.playlist.items = this.playlist.items.concat(playlist.items.map(PlaylistItem.create));

    // index playlist items by thier id
    this.reIndex();

    this.trigger({
      type: EVENT_CHANGE,
      playlist: this.playlist,
      added: playlist.items.length,
      loaded: 0,
      total: this.playlist.items.length
    });
  }

  /**
   * Retrieve the currently loaded playlist
   *
   * @return {array}
   */
  getPlaylist (asObject = false) {
    if (asObject) {
      return this.playlist;
    }
    return this.playlist.items;
  }

  /**
   * Retrieve a playlist item by it's index or uid
   *
   * @param {number|string} index The index or uid of the playlist item to retrieve
   *
   * @return {object|false}
   */
  getPlaylistItem (index) {
    if (isUndefined(index)) {
      index = this.playlistIndex;
    }

    if (isString(index)) {
      index = this.getPlaylistIndexByUid(index);
    }

    if (isUndefined(this.playlist.items) || isUndefined(this.playlist.items[index])) {
      return false;
    }

    return this.playlist.items[index];
  }

  /**
   * Retrieve the index of the current playlist item
   *
   * @return {number}
   */
  getPlaylistIndex () {
    if (this.playlistIndex === -1) {
      return false;
    }

    return this.playlistIndex;
  }

  /**
   * Find a playlist items index by it's UID
   *
   * @param {string} uid A playlist items UID
   *
   * @return {number}
   */
  getPlaylistIndexByUid (id) {
    if (!isUndefined(this.uidIndex[id])) {
      return this.uidIndex[id];
    }

    for (let i = 0, l = this.playlist.items.length; i < l; i++) {
      if (this.playlist.items[i].metadata.uid === id) {
        return i;
      }
    }

    return -1;
  }

  /**
   * Returns the metadata of the loaded playlist
   *
   * @returns {Object|null}
   */
  getPlaylistMetadata () {
    return (this.playlist && this.playlist.metadata) || null;
  }

  /**
   * Moves to the next item in the playlist when the current one finishes
   *
   * @param {object} event
   */
  nextPlaylistItem () {
    const nextIndex = this.playlistIndex + 1;

    if (nextIndex >= this.playlist.items.length) {
      // move to first playlist item when reaching the end
      if (this.repeat_ === REPEAT_MODE_PLAYLIST) {
        return this.selectPlaylistItem(0);
      }
      return -1;
    }

    return this.selectPlaylistItem(nextIndex);
  }

  /**
   * Move to the previous item in the playlist
   *
   * @return {number} The index of the new playlist item
   */
  previousPlaylistItem () {
    const prevIndex = this.playlistIndex - 1;

    if (prevIndex < 0) {
      // move to the last item when prev index is less than 0
      if (this.repeat_ === REPEAT_MODE_PLAYLIST) {
        return this.selectPlaylistItem(this.playlist.items.length - 1);
      }
      return -1;
    }
    return this.selectPlaylistItem(prevIndex);
  }

  /**
   * Check if a playlist item exists at the index
   *
   * @param  {number}  index The index to check
   *
   * @return {Boolean}
   */
  hasPlaylistItem (index) {
    return typeof this.playlist.items[index] !== 'undefined';
  }

  /**
   * Move to a playlist item by it's index or uid
   *
   * @param {number|string} index The index or uid of the playlist item to select
   *
   * @return {number} The index of the new playlist item
   */
  selectPlaylistItem (index) {
    if (isUndefined(index)) {
      return -1;
    }

    if (isString(index)) {
      index = this.getPlaylistIndexByUid(index);
    }

    // const playlistLen = this.playlist.items.length;

    // // Determine where to move to if the given index is larger than the playlist length
    // if (index >= playlistLen) {
    //   // If the index is greater than the length of the playlist, and the current
    //   // playlist item is NOT the last playlist item, then move to the last playlist item
    //   if (this.playlistIndex !== playlistLen - 1) {
    //     index = playlistLen - 1;
    //   } else {
    //     // If the index is greater than the length of the playlist, the current
    //     // playlist item is the last item in the playlist, and the 'repeat' setting
    //     // is set to 'playlist' then move to the first playlist item
    //     // @todo move playlist repeating to the player object instead of the playlist controller
    //     if (this.repeat_ === REPEAT_MODE_PLAYLIST) {
    //       index = 0;
    //     }
    //     // If the index is greater than the length of the playlist, the current item
    //     // is the last item in the playlist, and the 'repeat' setting is NOT set to 'playlist'
    //     // then stay where you are
    //     else {
    //       index = this.playlistIndex;
    //     }
    //   }
    // }

    // // Determine the index to move to if the selected index is less than zero
    // if (index < 0) {
    //   // When repeating playlists, move to the last playlist item
    //   if (this.repeat_ === REPEAT_MODE_PLAYLIST) {
    //     index = playlistLen - 1;
    //   } else {
    //     return false;
    //   }
    // }

    // Don't bother changing playlist items if the selected index is the same as the current index
    if (index === this.playlistIndex) {
      return -1;
    }

    // Stay on the current playlist item if the given index does not exist
    if (isUndefined(this.playlist.items[index])) {
      return -1;
    }

    this.logger.debug(`Playlist: Changing the active playlist item to ${index}`);

    // Update the current playlist index
    this.playlistIndex = index;

    this.trigger({
      type: EVENT_SELECT,
      playlistItem: this.getPlaylistItem(),
      playlistIndex: this.playlistIndex
    });

    return index;
  }

  /**
   * Destroy the playlist controller
   */
  destroy () {
    this.clearPlaylist();
  }

  /**
   * Unload current playing item from player
   */
  clearPlaylist () {
    this.playlist = [];
    this.playlistIndex = -1;
    this.uidIndex = {};
  }

  /**
   * Deselect the current active playlist item
   */
  deselectPlaylistItem () {
    this.playlistIndex = -1;
  }
}

PlaylistController.EVENT_CHANGE = EVENT_CHANGE;
PlaylistController.EVENT_SELECT = EVENT_SELECT;
